import { FormHelperText } from 'apollo-react';
import Button from 'apollo-react/components/Button';
import Card from 'apollo-react/components/Card';
import CardContent from 'apollo-react/components/CardContent';
import CardHeader from 'apollo-react/components/CardHeader';
import Container from 'apollo-react/components/Container';
import Grid from 'apollo-react/components/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from 'apollo-react/components/TextField';
import Typography from 'apollo-react/components/Typography';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../common/ConfirmDialog';
import ChangeNameDialog from '../common/ChangeNameDialog';
import { API_URL, OBJ_ROLE, SYS_ROLE } from '../common/Constants';
import DetailDialog from '../common/DetailDialog';
import Dialog from '../common/Dialog';
import Loading from '../common/Loading';
import { getApi, getFuncroleFlg, postApi, deleteApi, setURL, redirectToLogin } from '../common/Utils';
import { Box } from '@mui/material';
import './CreateFolder.scss';
import auditOrgProjList from '../json/auditOrgProjList.json';
import dummyFolderList from '../json/folderList.json';
import folderId from '../json/folderId.json';

/**
 * フォルダ作成
 */
function CreateFolder() {
	const [t] = useTranslation();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(true);
	const DIALOG_STATUS = {
		NO_DIALOG: '0',
		OK_DIALOG: '1',
		DETAIL_DIALOG: '2',
		ADD_FOLDER_CONFIRM_DIALOG: '3',
		DELETE_FOLDER_CONFIRM_DIALOG: '4',
		COMPLETE_DIALOG: '5',
		OK_DIALOG_BACK: '6',
		DETAIL_DIALOG_BACK: '7',
		SETTINGPERMISSION_CONFIRM_DIALOG: '8',
		CHANGE_FOLDERNAME: '9',
		CHANGE_CONFIRM: '10',
	};

	// 各種処理用
	const FOLDER_STATUS = {
		auditCreate: '0',
		auditDelete: '1',
		orgCreate: '2',
		orgDelete: '3',
		projectCreate: '4',
		projectDelete: '5',
		none: '9'
	}

	const [folderStatus, setFolderStatus] = useState(FOLDER_STATUS.none);

	// 確認ダイアログ表示用フラグ
	const [dialogStatus, setDialogStatus] = useState(DIALOG_STATUS.NO_DIALOG);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogContent, setDialogContent] = useState('');
	const [detailContent, setDetailContent] = useState('');
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [confirmDialogContent, setConfirmDialogContent] = useState('');
	const [changeDialogOpen, setChangeDialogOpen] = useState(false);
	const [changeDialogContent, setChangeDialogContent] = useState('');
	const [changeConfirmDialogOpen, setChangeConfirmDialogOpen] = useState(false);
	const [changeConfirmDialogContent, setChangeConfirmDialogContent] = useState('');

	// 各種権限
	const [serviceCodeAuth, setServiceCodeAuth] = useState(false);
	const [serviceCreateBtnAuth, setServiceCreateBtnAuth] = useState(false);
	const [serviceDeleteBtnAuth, setServiceDeleteBtnAuth] = useState(false);
	const [orgCodeAuth, setOrgCodeAuth] = useState(false);
	const [orgCreateBtnAuth, setOrgCreateBtnAuth] = useState(false);
	const [orgDeleteBtnAuth, setOrgDeleteBtnAuth] = useState(false);
	const [projCreateBtnAuth, setProjCreateBtnAuth] = useState(false);
	const [projDeleteBtnAuth, setProjDeleteBtnAuth] = useState(false);
	const [settingPermissionBtnAuth, setSettingPermissionBtnAuth] = useState(false);

	// フォルダ登録用情報の入力内容（APIで使用する）
	const [formValues, setFormValues] = useState({
		auditName: '',
		auditCode: '',
		orgName: '',
		orgLevel0Code: '',
		projectName: '',
		folderName: '',
		folderCode: '',
		changeFolderName: '',
		projectList: ([])
	});

	// サービス名リスト
	const [auditList, setAuditList] = useState([]);
	// ★★★ 追加：フィルタリング済みサービス名リスト ★★★
	const [filteredAuditList, setFilteredAuditList] = useState([]);
	// 企業名の選択リスト
	const [compList, setCompList] = useState([]);
	// プロジェクト名リスト
	const [projList, setProjList] = useState([]);
	// ユーザーロールコード
	const [usrroleCode, setUsrroleCode] = useState('');

	// ★★★ 高度な検索機能付きfilterAuditList関数 ★★★
	const filterAuditList = (inputValue) => {
		if (!inputValue || inputValue.trim() === '') {
			return []; // 空白時は空配列を返す
		}
		
		const searchTerm = inputValue.toLowerCase().trim();
		
		return auditList.filter(audit => {
			const label = audit.label.toLowerCase();
			// 前方一致または部分一致をチェック
			return label.startsWith(searchTerm) || label.includes(searchTerm);
		}).sort((a, b) => {
			const aLabel = a.label.toLowerCase();
			const bLabel = b.label.toLowerCase();
			const aStartsWith = aLabel.startsWith(searchTerm);
			const bStartsWith = bLabel.startsWith(searchTerm);
			
			// 1. 前方一致を優先
			if (aStartsWith && !bStartsWith) return -1;
			if (!aStartsWith && bStartsWith) return 1;
			
			// 2. 同じ優先度の場合は文字列順でソート
			return aLabel.localeCompare(bLabel);
		});
	};

	/**
	 * 共通エラー処理
	 * @param {object} error エラーオブジェクト
	 * @param {string} detailErrorMessage 詳細ダイアログに設定するエラーメッセージ
	 */
	const commonError = (error, detailErrorMessage) => {
		if (error.name === 'MaintenanceApiError') {
			navigate('/', { state: { maintenanceFlg: true, } });
		} else if (error.name === 'CustomApiError') {
			const message = error.message;
			setDialogStatus(DIALOG_STATUS.OK_DIALOG);
			setDialogOpen(true);
			setDialogContent(message);
		} else {
			setDialogStatus(DIALOG_STATUS.DETAIL_DIALOG);
			setDialogOpen(true);
			setDialogContent(detailErrorMessage);
			setDetailContent(error.stack);
		}
	};

	// 各種ボタンのハンドリング

	/**
	 * サービスフォルダ追加ボタン押下時の処理
	 */
	const handleAddServiceBtnClick = () => {
		var message = "";
		message = format(t('message.1002400'), t('service'));
		setDialogStatus(DIALOG_STATUS.ADD_FOLDER_CONFIRM_DIALOG);
		setFolderStatus(FOLDER_STATUS.auditCreate);
		setConfirmDialogOpen(true);
		setConfirmDialogContent(message)
	}
	/**
	 * サービスフォルダ削除ボタン押下時の処理
	 */
	const handleDeleteServiceBtnClick = () => {
		var message = "";
		message = format(t('message.1002800'), t('service'));
		setDialogStatus(DIALOG_STATUS.DELETE_FOLDER_CONFIRM_DIALOG);
		setFolderStatus(FOLDER_STATUS.auditDelete);
		setConfirmDialogOpen(true);
		setConfirmDialogContent(message)
	}
	/**
	 * 企業フォルダ追加ボタン押下時の処理
	 */
	const handleAddCompanyBtnClick = () => {
		var message = "";
		message = format(t('message.1002400'), t('org'));
		setDialogStatus(DIALOG_STATUS.ADD_FOLDER_CONFIRM_DIALOG);
		setFolderStatus(FOLDER_STATUS.orgCreate);
		setConfirmDialogOpen(true);
		setConfirmDialogContent(message)
	}
	/**
	 * 企業フォルダ削除ボタン押下時の処理
	 */
	const handleDeleteCompanyBtnClick = () => {
		var message = "";
		message = format(t('message.1002800'), t('org'));
		setDialogStatus(DIALOG_STATUS.DELETE_FOLDER_CONFIRM_DIALOG);
		setFolderStatus(FOLDER_STATUS.orgDelete);
		setConfirmDialogOpen(true);
		setConfirmDialogContent(message)
	}

	/**
	 * プロジェクトフォルダ追加ボタン押下時の処理
	 */
	const handleAddProjectBtnClick = () => {
		var message = "";
		message = format(t('message.1002400'), t('project'));
		setDialogStatus(DIALOG_STATUS.ADD_FOLDER_CONFIRM_DIALOG);
		setFolderStatus(FOLDER_STATUS.projectCreate);
		setConfirmDialogOpen(true);
		setConfirmDialogContent(message)
	}
	/**
	 * プロジェクトフォルダ削除ボタン押下時の処理
	 */
	const handleDeleteProjectBtnClick = () => {
		var message = "";
		message = format(t('message.1002800'), t('project'));
		setDialogStatus(DIALOG_STATUS.DELETE_FOLDER_CONFIRM_DIALOG);
		setFolderStatus(FOLDER_STATUS.projectDelete);
		setConfirmDialogOpen(true);
		setConfirmDialogContent(message)
	}

	/**
	 * ダイアログでOKボタン押下時の処理
	 */
	const onOk = () => {
		setDialogStatus(DIALOG_STATUS.NO_DIALOG);
		setDialogOpen(false);
		if (dialogStatus === DIALOG_STATUS.OK_DIALOG_BACK || dialogStatus === DIALOG_STATUS.DETAIL_DIALOG_BACK) {
		}
	};

	/**
	 * 確認ダイアログでキャンセルボタン押下時の処理
	 */
	const onCancel = () => {
		setDialogStatus(DIALOG_STATUS.NO_DIALOG);
		setConfirmDialogOpen(false);
	};

	/**
	 * キャンセルボタン押下時の処理
	 */
	const handleCancelClick = () => {
		commonBack();
	}

	/**
	 * 共通フォルダ管理画面遷移処理
	 */
	const commonBack = () => {
		// フォルダ管理画面へ遷移
		navigate('/folderManagement');
	};

	// 確認画面でOKをクリックした場合の処理
	const moveSettingPermission = () => {

		const param = {
			auditName: formValues.auditName,
			auditCode: formValues.auditCode,
			orgName: formValues.orgName,
			orgLevel0Code: formValues.orgLevel0Code,
			projectList: filteredProjList
		};
		// 権限設定へ遷移
		navigate('/settingpermission', { state: param })
	}

	/**
	 * 権限設定ボタン押下時の処理
	 */
	const handleSettingPermissionClick = () => {
		var message = "";
		message = t('message.1003200');
		setDialogStatus(DIALOG_STATUS.SETTINGPERMISSION_CONFIRM_DIALOG);
		setConfirmDialogOpen(true);
		setConfirmDialogContent(message)
	}

	/**
	 * フォルダ名の変更ボタン押下時の処理
	 */
	const handleChangeBtnClick = () => {
		setDialogStatus(DIALOG_STATUS.CHANGE_FOLDERNAME);
		setChangeDialogOpen(true);
		setChangeDialogContent(
			<React.Fragment>
				<Box display="flex" alignItems="center" width="100%">
					<div className='label'>
						変更前名称
					</div>
					<TextField
						className="textFieldChange"
						value={formValues.projectName}
						// helperText={validateMail(formValues.email)}
						disabled={true}
					// onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
					// FormHelperTextProps={{ className: 'red-helper-text' }}
					// placeholder={t('placeholder.1000400')}
					/>
				</Box>
				<Box display="flex" alignItems="center" width="100%">
					<div className='label'>
						変更後名称
					</div>
					<TextField
						className="textFieldChange"
						// helperText={validateMail(formValues.email)}
						onChange={(e) => setFormValues({ ...formValues, changeFolderName: e.target.value })}
					// FormHelperTextProps={{ className: 'red-helper-text' }}
					// placeholder={t('placeholder.1000400')}
					/>
				</Box>
			</React.Fragment>
		)
	}

	/**
	 * 変更確定の処理
	 */
	const openChangeNameConfirmWindow = () => {
		setDialogStatus(DIALOG_STATUS.CHANGE_CONFIRM);
		setChangeDialogOpen(false);
		setChangeConfirmDialogOpen(true);
		setChangeConfirmDialogContent(
			<React.Fragment className="changeConfirmDialog">
				<div className='confirmLabel'>
					変更前名称:　{formValues.projectName}
				</div>

				<div className='confirmLabel'>
					変更後名称:　{formValues.changeFolderName}
				</div>
				<Box display="flex" alignItems="center" width="100%">
					<div className='confirmLabel'>
						上記の{t('proj')}に変更しますがよろしいですか？
					</div>
				</Box>
			</React.Fragment>
		);
	}

	// API処理
	const changeFolderNameAPI = () => {
		const url = setURL(API_URL.FOLDER_UPDATE);
		let formData = new FormData();
		formData.append('auditCode', formValues.auditCode);
		formData.append('orgLevel0Code', formValues.orgLevel0Code);
		formData.append('beforeFolderName', formValues.projectName);
		formData.append('changeFolderCode', formValues.folderCode);
		formData.append('changeFolderName', formValues.changeFolderName);

		const postApiParam = {
			url: url,
			sysRole: SYS_ROLE.CREATE_FOLDER,
			body: formData,
			success: updateFolderSuccess,
			error: folderProcessError,
			json: folderId,
		};
		postApi(postApiParam);
	}


	// 作成処理
	const folderCreateProcess = async () => {

		let folderLevel = "1";
		let auditCode = "";
		let orgLevel0Code = "";
		let folderCode = "";
		let folderName = '';

		switch (folderStatus) {
			case FOLDER_STATUS.auditCreate:
				folderName = formValues.auditName;
				folderCode = formValues.auditCode;
				auditCode = formValues.auditCode;
				break;
			case FOLDER_STATUS.orgCreate:
				folderLevel = "2";
				auditCode = formValues.auditCode;
				folderName = formValues.orgName;
				folderCode = formValues.orgLevel0Code;
				orgLevel0Code = formValues.orgLevel0Code;
				break;
			case FOLDER_STATUS.projectCreate:
				folderLevel = "3";
				auditCode = formValues.auditCode;
				orgLevel0Code = formValues.orgLevel0Code;
				folderName = formValues.projectName;
				break;
		}
		const url = setURL(API_URL.FOLDER_CREATE);
		let formData = new FormData();
		formData.append('folderLevel', folderLevel);
		formData.append('auditCode', auditCode);
		formData.append('orgLevel0Code', orgLevel0Code);
		formData.append('creFolderName', folderName);
		formData.append('creFolderCode', folderCode);

		const postApiParam = {
			url: url,
			sysRole: SYS_ROLE.CREATE_FOLDER,
			body: formData,
			success: createFolderSuccess,
			error: folderProcessError,
			json: folderId,
		};
		await postApi(postApiParam);
	};

	// 削除処理
	const folderDeleteProcess = () => {
		let folderLevel = "1";
		let auditCode = "";
		let orgLevel0Code = "";
		let folderCode = "";
		let folderName = '';

		switch (folderStatus) {
			case FOLDER_STATUS.auditDelete:
				folderName = formValues.auditName;
				folderCode = formValues.auditCode;
				break;
			case FOLDER_STATUS.orgDelete:
				folderLevel = "2";
				auditCode = formValues.auditCode;
				folderName = formValues.orgName;
				folderCode = formValues.orgLevel0Code;
				break;
			case FOLDER_STATUS.projectDelete:
				folderLevel = "3";
				auditCode = formValues.auditCode;
				orgLevel0Code = formValues.orgLevel0Code;
				folderName = formValues.projectName;
				break;
		}
		const url = setURL(API_URL.FOLDER_DELETE);
		let formData = new FormData();
		formData.append('folderLevel', folderLevel);
		formData.append('auditCode', auditCode);
		formData.append('orgLevel0Code', orgLevel0Code);
		formData.append('delFolderName', folderName);
		formData.append('delFolderCode', folderCode);

		const postApiParam = {
			url: url,
			sysRole: SYS_ROLE.CREATE_FOLDER,
			body: formData,
			success: deleteFolderSuccess,
			error: folderProcessError,
		};
		deleteApi(postApiParam);
	}

	/**
	 * フォルダ追加APIの成功時の処理
	 */
	function createFolderSuccess(response) {
		setDialogStatus(DIALOG_STATUS.COMPLETE_DIALOG);
		const message = format(t('message.1002500'), "");
		setDialogOpen(true);
		setDialogContent(message);

		// 作成したフォルダのコード（企業IDや部署ID、フォルダID）を現在取得しているリストに再代入
		switch (folderStatus) {
			case FOLDER_STATUS.auditCreate:
				// const updatedAuditList = auditList.push({value: formValues.auditName,label: response.folderId});

				formValues.auditCode = response.folderId;

				const newCheck = { auditCode: formValues.auditCode }
				checkFolderList.push(newCheck);

				// formValues.auditNameの前後の半角・全角スペースをトリムして再代入
				formValues.auditName = formValues.auditName.replace(/^[\s　]+|[\s　]+$/g, '');
				const newAudit = { label: formValues.auditName, value: response.folderId };

				// valueが一致する要素が存在するか確認
				const exists = auditList.some(item => item.value === newAudit.value);

				// 存在しない場合のみ追加
				if (!exists) {
					auditList.push(newAudit);
				}

				// setAuditList(updatedAuditList);

				// 作成したサービス名について現在入力中の文字からスペースを削除する


				break;

			case FOLDER_STATUS.orgCreate:
				// const updatedCompList = [...compList, { ...label, value: response.folderId }];
				formValues.orgLevel0Code = response.folderId;

				const newCheckorg = { auditCode: formValues.auditCode, orgCode: formValues.orgLevel0Code }
				checkFolderList.push(newCheckorg);

				// 新しい値
				formValues.orgName = formValues.orgName.replace(/^[\s　]+|[\s　]+$/g, '');
				const newOrg = { label: formValues.orgName, value: response.folderId };

				// valueが一致する要素が存在するか確認
				const existsOrg = compList.some(item => item.value === newOrg.value);

				// 存在しない場合のみ追加
				if (!existsOrg) {
					compList.push(newOrg);
				}
				break;
			// setCompList(updatedCompList);
			case FOLDER_STATUS.projectCreate:

				formValues.projectName = formValues.projectName.replace(/^[\s　]+|[\s　]+$/g, '');
				const newProj = { auditCode: formValues.auditCode, orgLevel0Code: formValues.orgLevel0Code, projName: formValues.projectName };
				projList.push(newProj);
				filteredProjList.push(newProj);
				const isValid = filteredProjList.find(
					option => option.projName === formValues.projectName
				);
				setIsProjectNameValid(isValid);
				break;

		}
	};

	/**
	 * フォルダ追加APIの成功時の処理
	 */
	function updateFolderSuccess(response) {
		setDialogStatus(DIALOG_STATUS.COMPLETE_DIALOG);
		const message = format(t('message.1002600'), "");
		setDialogOpen(true);
		setDialogContent(message);

		const newProj2 = { auditCode: formValues.auditCode, orgLevel0Code: formValues.orgLevel0Code, projName: formValues.changeFolderName };

		const updateProjList = projList.map(proj => {
			if (
				proj.auditCode === newProj2.auditCode &&
				proj.orgLevel0Code === newProj2.orgLevel0Code &&
				proj.projName === formValues.projectName
			) {
				return newProj2;
			}
			return proj;
		});

		const updatefilProjList = filteredProjList.map(proj => {
			if (
				proj.auditCode === newProj2.auditCode &&
				proj.orgLevel0Code === newProj2.orgLevel0Code &&
				proj.projName === formValues.projectName
			) {
				return newProj2;
			}
			return proj;
		});

		setProjList(updateProjList);
		setFilteredProjList(updatefilProjList);

		// 変更ボタン切り替え
		const isValid2 = filteredProjList.find(
			option => option.projName === formValues.projectName
		);
		setIsProjectNameValid(isValid2);

		formValues.projectName = formValues.changeFolderName;
	};

	/**
	 * フォルダ削除APIの成功時の処理
	 */
	function deleteFolderSuccess() {
		setDialogStatus(DIALOG_STATUS.COMPLETE_DIALOG);
		const message = format(t('message.1002900'), "");
		setDialogOpen(true);
		setDialogContent(message);

		switch (folderStatus) {
			case FOLDER_STATUS.auditDelete:

				// 直接代入はできないので一度tmp用のconstに格納後に再度セットすること
				const newCheck = { auditCode: formValues.auditCode }
				const checkFolderListtmp = checkFolderList.filter(item => !(item.auditCode == newCheck.auditCode))
				setCheckFolderList(checkFolderListtmp);

				// ★★★ 追加：auditListからも削除 ★★★
				const updatedAuditList = auditList.filter(item => item.value !== formValues.auditCode);
				
				setAuditList(updatedAuditList);

				// ★★★ 追加：filteredAuditListからも削除 ★★★
				const updatedFilteredAuditList = filteredAuditList.filter(item => item.value !== formValues.auditCode);
				setFilteredAuditList(updatedFilteredAuditList);

				// 新しい値
				const newProj = {
					auditCode: formValues.auditCode,
				};

				// valueが一致する要素を削除
				const tmpprojList = projList.filter(item => 
					item.auditCode !== newProj.auditCode
				);
				setProjList(tmpprojList);

				formValues.auditName = "";
				formValues.orgName = "";
				formValues.projectName = "";

				break;

			case FOLDER_STATUS.orgDelete:

				// 直接代入はできないので一度tmp用のconstに格納後に再度セットすること
				const newCheckOrg = { auditCode: formValues.auditCode, orgCode: formValues.orgLevel0Code }
				const checkFolderListtmpOrg = checkFolderList.filter(item => !(item.auditCode == newCheckOrg.auditCode &&
					item.orgCode == newCheckOrg.orgCode)
				)
				setCheckFolderList(checkFolderListtmpOrg);

				// 新しい値
				const newProj2 = {
					auditCode: formValues.auditCode,
					orgLevel0Code: formValues.orgLevel0Code,
				};

				// valueが一致する要素を削除
				const tmpprojList2 = projList.filter(item => 
					item.auditCode !== newProj2.auditCode &&
					item.orgLevel0Code !== newProj2.orgLevel0Code
				);
				setProjList(tmpprojList2);

				formValues.orgName = "";
				formValues.projectName = "";

				break;

			case FOLDER_STATUS.projectDelete:

				// 直接代入はできないので一度tmp用のconstに格納後に再度セットすること
				const newCheckProj = { auditCode: formValues.auditCode, orgCode: formValues.orgLevel0Code, folderName: formValues.projectName }
				const checkFolderListtmpProj = checkFolderList.filter(item => !(item.auditCode == newCheckProj.auditCode &&
					item.orgCode == newCheckProj.orgCode && item.folderName == newCheckProj.folderName)
				)
				setCheckFolderList(checkFolderListtmpProj);

				// 新しい値
				const newProj3 = {
					auditCode: formValues.auditCode,
					orgLevel0Code: formValues.orgLevel0Code,
					projName: formValues.projectName
				};

				// valueが一致する要素を削除
				const tmpprojList3 = projList.filter(item => !(
					item.auditCode == newProj3.auditCode &&
					item.orgLevel0Code == newProj3.orgLevel0Code &&
					item.projName == newProj3.projName
				)
				);
				setProjList(tmpprojList3);

				formValues.projectName = "";

				break;
		}
	};

	/**
	 * フォルダ処理APIの失敗時の処理
	 * @param {object} error エラーオブジェクト
	 */
	function folderProcessError(error) {
		if (error.name === 'MaintenanceApiError') {
			navigate('/', { state: { maintenanceFlg: true, } });
		} else if (error.name === 'CustomApiError') {
			//入力テキストに禁則文字が含まれている場合、azureのインフラルールによりエラーとなる
			//ただし、エラーは403 Forbiddenとしか返って来ない（＝禁則文字によるエラーだと判別できない）ため、メッセージを変更する
			const forbidCharPat1 = /.+403 Forbidden.+/;
			const forbidCharPat2 = /.+Microsoft-Azure-Application-Gateway.+/;
			if ((error.message.match(forbidCharPat1)) && (error.message.match(forbidCharPat2))) {
				const message = t('message.1000810');
				setDialogStatus(DIALOG_STATUS.OK_DIALOG);
				setDialogOpen(true);
				setDialogContent(message);

				//認証期限切れの場合は再ログイン
				redirectToLogin();
			} else {
				const message = error.message;
				setDialogStatus(DIALOG_STATUS.OK_DIALOG_BACK);
				setDialogOpen(true);
				setDialogContent(message);

				//認証期限切れの場合は再ログイン
				redirectToLogin();
			}
		} else {
			let message = '';
			if (folderStatus === 0 || folderStatus === 2 || folderStatus === 4) {
				message = t('errormsg.1000300');
			}
			else {
				message = t('errormsg.1000400');
			}
			setDialogStatus(DIALOG_STATUS.DETAIL_DIALOG);
			setDialogOpen(true);
			setDialogContent(message);
			setDetailContent(error.stack);

			//認証期限切れの場合は再ログイン
			redirectToLogin();
		}

		//認証期限切れの場合は再ログイン
		redirectToLogin();
	};

	/**
	 * サービス名取得APIの成功時の処理
	 * @param {array} response 
	 */
	function auditListApiSuccess(response) {
		// 取得したレスポンスを各リストに格納

		// サービス名
		const auditList = response.auditList.flatMap(item =>
		({
			value: item.auditCode,
			label: item.auditName
		})
		);
		setAuditList(auditList);

		// ★★★ 修正：初期表示時は空配列を設定 ★★★
		setFilteredAuditList([]);

		// 企業名
		const compList = response.compList.flatMap(item =>
		({
			value: item.orgLevel0Code,
			label: item.comp
		})
		);
		setCompList(compList);

		// プロジェクト名
		const projList = response.projList.flatMap(item =>
		({
			auditCode: item.auditCode,
			orgLevel0Code: item.orgLevel0Code,
			projCode: item.projCode,
			projName: item.projName
		})
		);
		setProjList(projList);
	};

	/**
	 * サービス名取得APIの失敗時の処理
	 * @param {object} error エラーオブジェクト
	 */
	function auditListApiError(error) {
		const detailErrorMessage = t('errormsg.1000000');
		commonError(error, detailErrorMessage);
	};

	// 作成済みフォルダかどうかをリストに格納しておく
	const [checkFolderList, setCheckFolderList] = useState();

	/**
	 * 作成済みフォルダ取得APIの成功時の処理
	 * @param {array} response 
	 */
	function getCheckListApiSuccess(response) {
		// サービス名
		setCheckFolderList(response);
	};

	/**
	 * サービス名取得APIの失敗時の処理
	 * @param {object} error エラーオブジェクト
	 */
	function getCheckListApiError(error) {
		const detailErrorMessage = t('errormsg.1000000');
		commonError(error, detailErrorMessage);
	};

	// 各項目バリデーション
	const [auditNameVali, setAuditNameVali] = useState(false);
	const [orgNameVali, setOrgNameVali] = useState(false);
	const [projectNameVali, setProjectNameVali] = useState(false);
	const [changepFolderNameVali, setchangepFolderNameVali] = useState(false);

	// サービス名とサービスコードのバリデーション
	const validateAuditName = () => {
		if (formValues.auditName === '' && formValues.auditCode === '') {
			return t('message.1003000');
		}
		if (formValues.auditName === '' || formValues.auditName === undefined) {
			return format(t('message.1000220'), t('service'));
		}
		if (formValues.auditName.length > 60) {
			return format(t('message.1000300'), t('service'), 60);
		}
		// if (formValues.auditCode === '' || formValues.auditCode === undefined) {
		// 	return format(t('message.1000220'), t('auditCode'));
		// }
		// if (formValues.auditCode.length > 10) {
		// 	return format(t('message.1000300'), t('auditCode'), 10);
		// }
		// if (!formValues.auditCode.match(/^[A-Za-z0-9]*$/)) {
		// 	return format(t('message.1000310'), t('auditCode'));
		// } 
		return '';
	};

	// 企業名のバリデーション
	const validateOrgName = () => {
		if (formValues.orgName === '' && formValues.orgLevel0Code === '') {
			return t('message.1003100');
		}
		if (formValues.orgName === '') {
			return format(t('message.1000200'), t('org'));
		}
		if (formValues.orgName.length > 100) {
			return format(t('message.1000300'), t('org'), 100);
		}
		// if (formValues.orgLevel0Code === '') {
		// 	return format(t('message.1000200'), t('orgCode'));
		// }
		// if (formValues.orgLevel0Code.length > 10) {
		// 	return format(t('message.1000300'), t('orgCode'), 10);
		// }
		// if (!formValues.orgLevel0Code.match(/^[A-Za-z0-9]*$/)) {
		// 	return format(t('message.1000310'), t('orgCode'));
		// } 
		return '';
	};

	// プロジェクト名のバリデーション
	const validateProjectName = () => {
		if (formValues.projectName === '') {
			return format(t('message.1003150'), t('project'));
		}
		if (formValues.projectName.length > 60) {
			return format(t('message.1000300'), t('project'), 60);
		}
		return '';
	};

	// C#のプレースホルダーをJavascriptで使用するための処理
	const format = (str, ...args) => {
		for (const [i, arg] of args.entries()) {
			const regExp = new RegExp(`\\{${i}\\}`, 'g')
			str = str.replace(regExp, arg)
		}
		return str
	}

	// フィルター用のリスト保存
	const [filteredProjList, setFilteredProjList] = useState([]);

	// プロジェクト名のフィルタリングをするための処理
	useEffect(() => {
		const selected = projList.filter(proj => proj.auditCode === formValues.auditCode && proj.orgLevel0Code === formValues.orgLevel0Code);
		setFilteredProjList(selected);
	}, [formValues.auditName, formValues.auditCode, formValues.orgName, formValues.orgLevel0Code, projList]);

	// 変更ボタンのだし分け用フラグ変数
	const [isProjectNameValid, setIsProjectNameValid] = useState(false);

	// 変更ボタンの活性・非活性切り替え
	useEffect(() => {
		const isValid = filteredProjList.find(
			option => option.projName === formValues.projectName
		);
		setIsProjectNameValid(isValid);
	}, [formValues.projectName, filteredProjList,projList]);

	useEffect(() => {
		(async () => {
			if (window.localStorage.getItem(['token']) === null) {
				navigate('/')
				return;
			}

			// 権限系設定

			// ログイン中ユーザの権限取得
			const usrroleCodeData = JSON.parse(localStorage.getItem('usrroleCode'));
			const usrroleCode = usrroleCodeData.usrroleCode;
			setUsrroleCode(usrroleCode);

			// サービスコードの権限フラグを設定
			const auditCodeAuth = getFuncroleFlg(SYS_ROLE.CREATE_FOLDER, OBJ_ROLE.SERVICE_CODE);
			setServiceCodeAuth(auditCodeAuth);

			// サービス作成ボタンの権限フラグを設定
			const serviceCreateAuth = getFuncroleFlg(SYS_ROLE.CREATE_FOLDER, OBJ_ROLE.SERVICE_CREATE_BTN);
			setServiceCreateBtnAuth(serviceCreateAuth);

			// サービス削除ボタンの権限フラグを設定
			const serviceDeleteAuth = getFuncroleFlg(SYS_ROLE.CREATE_FOLDER, OBJ_ROLE.SERVICE_DELETE_BTN);
			setServiceDeleteBtnAuth(serviceDeleteAuth);

			// サービスコードの権限フラグを設定
			const orgCodeAuth = getFuncroleFlg(SYS_ROLE.CREATE_FOLDER, OBJ_ROLE.ORG_CODE);
			setOrgCodeAuth(orgCodeAuth);

			// 企業作成ボタンの権限フラグを設定
			const orgCreateAuth = getFuncroleFlg(SYS_ROLE.CREATE_FOLDER, OBJ_ROLE.ORG_CREATE_BTN);
			setOrgCreateBtnAuth(orgCreateAuth);

			// 企業削除ボタンの権限フラグを設定
			const orgDeleteAuth = getFuncroleFlg(SYS_ROLE.CREATE_FOLDER, OBJ_ROLE.ORG_DELETE_BTN);
			setOrgDeleteBtnAuth(orgDeleteAuth);

			// プロジェクト作成ボタンの権限フラグを設定
			const projectCreateAuth = getFuncroleFlg(SYS_ROLE.CREATE_FOLDER, OBJ_ROLE.PROJ_CREATE_BTN);
			setProjCreateBtnAuth(projectCreateAuth);

			// プロジェクト削除ボタンの権限フラグを設定
			const projectDeleteAuth = getFuncroleFlg(SYS_ROLE.CREATE_FOLDER, OBJ_ROLE.PROJ_DELETE_BTN);
			setProjDeleteBtnAuth(projectDeleteAuth);

			// 権限設定ボタンの権限フラグを設定
			const settingPermissionBtnAuth = getFuncroleFlg(SYS_ROLE.CREATE_FOLDER, OBJ_ROLE.SETTING_PERMISSION_BTN);
			setSettingPermissionBtnAuth(settingPermissionBtnAuth);

			// サービス名と企業を取得
			const auditurl = setURL(API_URL.AUDIT_ORG_PROJ_LIST);
			const getRoleApiParam = {
				url: auditurl,
				sysRole: SYS_ROLE.CREATE_FOLDER,
				success: auditListApiSuccess,
				error: auditListApiError,
				json: auditOrgProjList,
			}
			await getApi(getRoleApiParam);

			// フォルダを作成済みかどうか判定してそれをリストに入れる
			const checkurl = setURL(API_URL.FOLDER_CHECK);
			const getApiParam = {
				url: checkurl,
				sysRole: SYS_ROLE.CREATE_FOLDER,
				success: getCheckListApiSuccess,
				error: getCheckListApiError,
				json: dummyFolderList,
			}
			await getApi(getApiParam);

			setIsLoading(false);
		})();
	}, []);

	// 各種ボタン用フラグ変数
	const [auditCodeFlg, setAuditCodeFlg] = useState(false);
	const [compCodeFlg, setCompCodeFlg] = useState(false);

	// バリデーションチェック用useEffect
	useEffect(() => {
		const validationMessage = validateAuditName(formValues.auditName);
		setAuditNameVali(validationMessage === '');

		const matchedAudit = auditList.find(audit => audit.label === formValues.auditName);
		if (matchedAudit) {
			formValues.auditCode = matchedAudit.value;
		} else {
			formValues.auditCode = "";
		}

		if (checkFolderList != undefined) {
			const matchedFolderAudit = checkFolderList.find(check => check.auditCode === formValues.auditCode);
			if (formValues.auditCode != null && matchedFolderAudit != undefined) {
				setAuditCodeFlg(true);
			} else {
				setAuditCodeFlg(false);
			}
		}


	});

	useEffect(() => {
		const validationMessage = validateOrgName(formValues.orgName);
		setOrgNameVali(validationMessage === '');

		const matchedAudit = compList.find(comp => comp.label === formValues.orgName);
		if (matchedAudit) {
			formValues.orgLevel0Code = matchedAudit.value;
		} else {
			formValues.orgLevel0Code = "";
		}

		if (checkFolderList != undefined) {
			// const matchedFolderAudit = checkFolderList.find(check => check.orgCode.includes(formValues.orgLevel0Code));
			const matchedFolderOrg = checkFolderList.find(check =>
				check.auditCode === formValues.auditCode &&
				check.orgCode === formValues.orgLevel0Code
			);
			if (formValues.auditCode != null && formValues.orgLevel0Code != null && formValues.orgLevel0Code !== "" && matchedFolderOrg != undefined) {
				setCompCodeFlg(true);
			} else {
				setCompCodeFlg(false);
			}
		}

	});

	useEffect(() => {
		const validationMessage = validateProjectName(formValues.projectName);
		setProjectNameVali(validationMessage === '');
	}, [formValues.projectName]);

	useEffect(() => {
		const validationMessage = validateProjectName(formValues.changeFolderName);
		setchangepFolderNameVali(validationMessage === '');
	}, [formValues.changeFolderName]);

	// リストに含まれているかを判定する処理
	const containsKeyword = (list, keyword) => {
		return list.some(item => item.label && item.label === keyword);
	};

	if (isLoading) {
		// ロード中の場合はローディング画面
		return (
			<Loading />
		);
	} else {
		return (
			<Container className="createFolder" maxWidth="lg">

				<Card className="card">
					<CardHeader
						title={t('foldercreateTitle')}
						className="custom-card-header"
					/>
					<CardContent>
						<Grid container>

							<div className='desctiption'>{t('foldercreateDescription')}</div>

							{/* サービス */}
							<Box display="flex" alignItems="center" width="100%">
								<Typography
									className="label"
								>
									{t('service')}
								</Typography>
								<Autocomplete
									className='inputlist'
									options={filteredAuditList} // ★★★ 変更：auditList → filteredAuditList ★★★
									freeSolo
									renderInput={(params) => <TextField {...params} />}
									inputValue={formValues.auditName}
									onInputChange={(event, newValue) => {
										setFormValues({ ...formValues, auditName: newValue });
										// ★★★ 追加：入力値に基づいてリストをフィルタリング ★★★
										const filtered = filterAuditList(newValue);
										setFilteredAuditList(filtered);
										
									}}
									onChange={(event, newValue) => {
										if (typeof newValue === 'string') {
											setFormValues({ ...formValues, auditName: newValue, auditCode: '' });
										} else if (newValue && newValue.label && newValue.value) {
											setFormValues({ ...formValues, auditName: newValue.label, auditCode: newValue.value });
										} else {
											setFormValues({ ...formValues, auditName: '', auditCode: '' });
										}
									}
									}
									// ★★★ 追加：フィルタリングを無効化（手動で制御するため） ★★★
        							filterOptions={(options) => options}
								/>
								{/* <Typography
									className="labelmargin1"
								>
									{t('auditCode')}
								</Typography>
								<TextField
									className="textFieldCode"
									value={formValues.auditCode}
									disabled={auditCodeFlg || !serviceCodeAuth}
									onChange={(e) => setFormValues({ ...formValues, auditCode: e.target.value })}
								/> */}
								<Button
									className="createBtn"
									variant="primary"
									disabled={!auditNameVali || !serviceCreateBtnAuth || auditCodeFlg}
									// disabled={!auditNameVali || !serviceCreateBtnAuth}
									onClick={handleAddServiceBtnClick}
								>
									{t('create')}
								</Button>
								{/* <Button
									className="updateBtn"
									variant="primary"
									disabled={!auditNameVali || !orgNameVali || !orgCreateBtnAuth}
									// onClick={handleAddCompanyBtnClick}
								>
									{t('update')}
								</Button> */}
								<div className='dummyBtnSpacer'></div>
								{serviceDeleteBtnAuth && <Button
									className="deleteBtn"
									variant="primary"
									onClick={handleDeleteServiceBtnClick}
									disabled={!serviceDeleteBtnAuth || !auditNameVali || !auditCodeFlg}
								// disabled={!serviceDeleteBtnAuth || !auditNameVali}
								>
									{t('delete')}
								</Button>
								}
							</Box>
							<Box display="flex" alignItems="center" width="100%">
								<FormHelperText
									className='red-helper-text'
								>{validateAuditName()}</FormHelperText>
							</Box>
							{/* サービス　ここまで */}

							{/* 企業 */}
							<Box display="flex" alignItems="center" width="100%">
								<Typography
									className="label"
								>
									{t('org')}
								</Typography>
								<Autocomplete
									className='inputlist'
									options={compList}
									freeSolo
									renderInput={(params) => <TextField {...params} />}
									inputValue={formValues.orgName}
									onInputChange={(event, newValue) => {
										setFormValues({ ...formValues, orgName: newValue });
									}}
									onChange={(event, newValue) => {
										if (typeof newValue === 'string') {
											setFormValues({ ...formValues, orgName: newValue, orgLevel0Code: '' });
										} else if (newValue && newValue.label && newValue.value) {
											setFormValues({ ...formValues, orgName: newValue.label, orgLevel0Code: newValue.value });
										} else {
											setFormValues({ ...formValues, orgName: '', orgLevel0Code: '' });
										}
									}
									}
								/>
								{/* <Typography
									className="labelmargin1"
								>
									{t('orgCode')}
								</Typography>
								<TextField
									className="textFieldCode"
									value={formValues.orgLevel0Code}
									disabled={compCodeFlg || !orgCodeAuth}
									onChange={(e) => setFormValues({ ...formValues, orgLevel0Code: e.target.value })}
								/> */}
								<Button
									className="createBtn"
									variant="primary"
									disabled={!auditNameVali || !orgNameVali || !orgCreateBtnAuth || compCodeFlg}
									// disabled={!auditNameVali || !orgNameVali || !orgCreateBtnAuth}
									onClick={handleAddCompanyBtnClick}
								>
									{t('create')}
								</Button>
								{/* <Button
									className="updateBtn"
									variant="primary"
									disabled={!auditNameVali || !orgNameVali || !orgCreateBtnAuth}
									// onClick={handleAddCompanyBtnClick}
								>
									{t('update')}
								</Button> */}
								<div className='dummyBtnSpacer'></div>
								{orgDeleteBtnAuth && <Button
									className="deleteBtn"
									variant="primary"
									onClick={handleDeleteCompanyBtnClick}
									disabled={!orgDeleteBtnAuth || !orgNameVali || !auditNameVali || !compCodeFlg}
								// disabled={!orgDeleteBtnAuth || !orgNameVali || !auditNameVali}
								>
									{t('delete')}
								</Button>
								}
							</Box>
							<Box display="flex" alignItems="center" width="100%">
								<FormHelperText
									className='red-helper-text'
								>{validateOrgName()}</FormHelperText>
							</Box>
							{/* 企業　ここまで */}

							<Box display="flex" alignItems="center" width="100%">
								<Typography className="label">{t('proj')}</Typography>
								<Autocomplete
									disablePortal
									className='inputlist'
									freeSolo
									options={filteredProjList}
									getOptionLabel={(option) => option.projName || ''}
									renderInput={(params) => <TextField {...params} />}
									inputValue={formValues.projectName}
									onInputChange={(event, newValue) => {
										setFormValues({ ...formValues, projectName: newValue });
									}}
									onChange={(event, newValue) => {
										if (typeof newValue === 'string') {
											setFormValues({ ...formValues, projectName: newValue });
										} else if (newValue && newValue.projectName) {
											setFormValues({ ...formValues, projectName: newValue.projectName });
										} else {
											setFormValues({ ...formValues, projectName: '' });
										}
									}}
								/>
								<Button
									className="createBtn"
									variant="primary"
									disabled={!auditNameVali || !orgNameVali || !projectNameVali || !projCreateBtnAuth || isProjectNameValid}
									onClick={handleAddProjectBtnClick}
								>
									{t('create')}
								</Button>
								<Button
									className="updateBtn"
									variant="primary"
									disabled={!auditNameVali || !orgNameVali || !orgCreateBtnAuth || !isProjectNameValid}
									onClick={handleChangeBtnClick}
								>
									{t('update')}
								</Button>
								{projDeleteBtnAuth && <Button
									className="deleteBtn"
									variant="primary"
									onClick={handleDeleteProjectBtnClick}
									disabled={!projDeleteBtnAuth || !orgNameVali || !projectNameVali || !auditNameVali || !isProjectNameValid}
								>
									{t('delete')}
								</Button>
								}
							</Box>
							<Box display="flex" alignItems="center" width="100%">
								<FormHelperText
									className='red-helper-text'
								>{validateProjectName(formValues.projectName)}</FormHelperText>
							</Box>
						</Grid>
						<Grid container justifyContent="flex-end" className='button'>
							<Button
								className="permissionSettingBtn"
								variant="primary"
								onClick={handleSettingPermissionClick}
								disabled={!settingPermissionBtnAuth || !auditNameVali || !orgNameVali}
							>
								{t('settingPermission')}
							</Button>
							<Button
								className="cancelBtn"
								variant="secondary"
								onClick={handleCancelClick}
							>
								{t('back')}
							</Button>
						</Grid>
					</CardContent>
				</Card>
				{(dialogStatus === DIALOG_STATUS.OK_DIALOG || dialogStatus === DIALOG_STATUS.OK_DIALOG_BACK) && (
					<Dialog
						isOpen={dialogOpen}
						content={dialogContent}
						onOk={onOk}
					/>
				)}
				{(dialogStatus === DIALOG_STATUS.DETAIL_DIALOG || dialogStatus === DIALOG_STATUS.DETAIL_DIALOG_BACK) && (
					<DetailDialog
						isOpen={dialogOpen}
						content={dialogContent}
						detailContent={detailContent}
						onOk={onOk}
					/>
				)}
				{dialogStatus === DIALOG_STATUS.ADD_FOLDER_CONFIRM_DIALOG && (
					<ConfirmDialog
						isOpen={confirmDialogOpen}
						content={confirmDialogContent}
						onOk={folderCreateProcess}
						onCancel={onCancel}
					/>
				)}
				{dialogStatus === DIALOG_STATUS.DELETE_FOLDER_CONFIRM_DIALOG && (
					<ConfirmDialog
						isOpen={confirmDialogOpen}
						content={confirmDialogContent}
						onOk={folderDeleteProcess}
						onCancel={onCancel}
					/>
				)}
				{dialogStatus === DIALOG_STATUS.COMPLETE_DIALOG && (
					<Dialog
						isOpen={dialogOpen}
						content={dialogContent}
						onOk={onOk}
					/>
				)}
				{dialogStatus === DIALOG_STATUS.SETTINGPERMISSION_CONFIRM_DIALOG && (
					<ConfirmDialog
						isOpen={confirmDialogOpen}
						content={confirmDialogContent}
						onOk={moveSettingPermission}
						onCancel={onCancel}
					/>
				)}
				{dialogStatus === DIALOG_STATUS.CHANGE_FOLDERNAME && (
					<ChangeNameDialog
						title={t('changeFolderName')}
						isOpen={changeDialogOpen}
						content={changeDialogContent}
						onOk={openChangeNameConfirmWindow}
						onCancel={onCancel}
						buttonConfirmTitle={t('update')}
						buttonCancelTitle={t('cancel')}
					/>
				)}
				{dialogStatus === DIALOG_STATUS.CHANGE_CONFIRM && (
					<ChangeNameDialog
						title={t('changeFolderName')}
						isOpen={changeConfirmDialogOpen}
						content={changeConfirmDialogContent}
						onOk={changeFolderNameAPI}
						onCancel={onCancel}
						buttonConfirmTitle={t('confirm')}
						buttonCancelTitle={t('cancel')}
					/>
				)}

			</Container>
		);
	}
}
export default CreateFolder;
